import React, { useState } from "react";
import { ClientUser } from "../../models/UserProfile";
import { useApiClient } from '../../components/useApiClient';
import useAuthToken from '../../functions/useAuthToken';

import ArtworkPaymentScheduleDialog from "./ArtworkPaymentScheduleDialog";
import ArtworkPaymentScheduleEditDialog from "../../components/Payments/ArtworkPaymentScheduleEditDialog";
import PaymentsPanel from "./PaymentsPanel"
import { Artwork } from "../../models/Artwork";
import { Valuation, ValuationHeaderData } from '../../models/Valuation';

import { DiaryItemUpsertDto, DiaryItemDto} from "../../models/diaryTypes";
import DiaryItemModal, { ViewMode } from "../DiaryManagement/DiaryItemModal";

import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../styles/mui_theme";
interface Props {
    artwork: Artwork | null;
    reloadToken: number;
    onRefresh: () => void;
}

const PaymentsTab: React.FC<Props> = ({
    artwork,
    reloadToken,
    onRefresh,
}) => {
    // APIs
    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();
    const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);

    const [payOpen, setPayOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [valuationHeader, setValuationHeader] = useState<ValuationHeaderData | null>(null);
    const [acquisitionDetails, setAcquisitionDetails] = useState<Valuation | null>(null);

    //Diary
    const [diaryModalOpen, setDairyModalOpen] = useState(false);
    const [editingDiaryItem, setEditingDiaryItem] = useState<DiaryItemDto | null>(null);
    const [modalViewMode, setModalViewMode] = useState<ViewMode>("edit");

    // Edit Payment Schedule State
    const [
        selectedPaymentScheduleId,
        setSelectedPaymentScheduleId,
    ] = useState<number | null>(null);

    const [
        editPaymentScheduleOpen,
        setEditPaymentScheduleOpen,
    ] = useState(false);

    function editPaymentItem(id: number) {
        setSelectedPaymentScheduleId(id);
        setEditPaymentScheduleOpen(true);
    }

    const handlePaymentUpdate = () => {
        setEditPaymentScheduleOpen(false);
    }


    async function handleCloseDiary() {
        setDairyModalOpen(false);
        setEditingDiaryItem(null);
    }

    async function createDiaryItem(dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary`, accessToken, JSON.stringify(dto))

            return (res) as { id: number };
        }
        catch (e) {
            console.error('Error in createDiaryItem', e);
            throw new Error('Error in createDiaryItem');
        }
    }

    async function updateDiaryItem(id: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in updateDiaryItem');

            const res = await putJson(`diary/${id}`, accessToken, JSON.stringify(dto));
        }
        catch (e) {
            console.error('Error in updateDiaryItem', e);
            throw new Error('Error in updateDiaryItem');
        }
    }

    async function getDiaryItemById(id: number, includeChildren: boolean) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in getDiaryItemById');

            const qs = includeChildren ? "?includeChildren=true" : "";

            const res = await get<DiaryItemDto>(`diary/${id}${qs}`, accessToken,);
            return res;
        }
        catch (e) {
            console.error('Error in getDiaryItemById', e);
            throw new Error('Error in getDiaryItemById');
        }
    }

    async function refreshDiaryItem(id: number) {
        const refreshed = await getDiaryItemById(id, true);
        setEditingDiaryItem(refreshed);
        return refreshed;
    }

    async function regenerateSeries(id: number, fromUtc?: string, toUtc?: string) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${id}/regenerate`, accessToken, JSON.stringify({ fromUtc, toUtc }))

            return (res);
        }
        catch (e) {
            throw new Error('Error in regenerateSeries');
        }
    }

    async function createChildDiaryItem(parentId: number, dto: DiaryItemUpsertDto) {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error('Invalid access token in createDiaryItem');

            const res = await postJson(`diary/${parentId}/children`, accessToken, JSON.stringify(dto))

            return (res);
        }
        catch (e) {
            console.error('Error in regenerateSeries', e);
            throw new Error('Error in regenerateSeries');
        }
    }

    async function onSaveDiary(dto: DiaryItemUpsertDto, id?: number) {
        if (!id) {
            await createDiaryItem(dto);
        } else {
            await updateDiaryItem(id, dto);
        }
        setDairyModalOpen(false);
        setEditingDiaryItem(null);
    }

    function openDiaryItem(id: number) {
        refreshDiaryItem(id);
        setDairyModalOpen(true);
    }

    React.useEffect(() => {

        const fetchValuationHeader = async () => {
            if (!artwork) return;

            try {
                setIsLoading(true);

                const accessToken = await getAccessToken();
                if (!accessToken) throw new Error('Invalid access token in payments loading');

                const res = await get<ValuationHeaderData>(`valuations/header/${artwork.artworkId}`, accessToken,);
                setValuationHeader(res);

                if (res?.acquisition) {
                    setAcquisitionDetails(res.acquisition);
                } else {
                    setAcquisitionDetails(null);
                }

            } catch (error) {
                console.error('Failed to fetch valuation:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchValuationHeader();

    },[artwork]);

    return (
            <ThemeProvider theme={theme}>
            {artwork &&
                <PaymentsPanel
                    artworkId={artwork.artworkId}
                    onAdd={() => setPayOpen(true)}
                    reloadToken={reloadToken}
                    onOpenSchedule={(parentDiaryItemId) => openDiaryItem(parentDiaryItemId)}
                    onEditSchedule={(paymentScheduleId) => editPaymentItem(paymentScheduleId)}
                />
            }

            {artwork && payOpen &&
                <ArtworkPaymentScheduleDialog
                key={artwork.artworkId}
                open={payOpen}
                artworkId={artwork.artworkId}
                artworkTitle={artwork.artWorkTitle}
                valuationDefault={acquisitionDetails?.lowerRangeAmount ?? 0}
                currencyCodeDefault={acquisitionDetails?.currencyCode ?? "GBP"}
                onClose={() => setPayOpen(false)}
                onCreated={(r) => {
                    setPayOpen(false);
                    openDiaryItem(r.parentDiaryItemId);
                    onRefresh()
                }}
                />
            }

            {editingDiaryItem && editingDiaryItem !== null &&
                <DiaryItemModal
                    open={diaryModalOpen}
                    item={editingDiaryItem}
                    viewMode={modalViewMode}
                    clientUsers={clientUsers}
                    onClose={handleCloseDiary}
                    onSave={onSaveDiary}
                    refreshParent={refreshDiaryItem}
                    regenerateSeries={regenerateSeries}
                    updateDiaryItem={updateDiaryItem}
                    createChildDiaryItem={createChildDiaryItem}
                />
            }

            {artwork && selectedPaymentScheduleId !== null && (
                <ArtworkPaymentScheduleEditDialog
                    key={selectedPaymentScheduleId}
                    open={editPaymentScheduleOpen}
                    paymentScheduleId={selectedPaymentScheduleId}
                    artworkId={artwork.artworkId}
                    onClose={() => {
                        setEditPaymentScheduleOpen(false);
                        setSelectedPaymentScheduleId(null);
                    }}
                    onUpdated={() => {
                        handlePaymentUpdate();
                    }}
                />
            )}
            </ThemeProvider>
    );
};

export default PaymentsTab;
